import { Injectable, inject } from '@angular/core';
import { IThemeConfig } from 'alpha-config-interfaces';
import { Observable, lastValueFrom, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { AlphaHttpClient } from './alpha-http-client';
import { BranchStateService } from './branch-state.service';
import { ProjectService } from './project.service';

@Injectable({ providedIn: 'root' })
export class UIService {
    private httpClient = inject(AlphaHttpClient);
    private branchStateService = inject(BranchStateService);
    private projectService = inject(ProjectService);

    icons = [];
    private domainUrl = {
        type: 'environment',
        value: {
            __alpha_env__: 'ALPHA_UI_CONFIG_SERVICE_URL',
        },
    };
    cssVersionData!: any;
    cssObject: any;

    saveCss(cssConfig: { version: string; css: string }) {
        const project = this.projectService.fetchCurrentProject();
        return this.httpClient.post(this.domainUrl, `/ui/css/save`, {
            projectId: project.projectId,
            ...cssConfig,
        });
    }

    fetchCss(): Observable<any> {
        const project = this.projectService.fetchCurrentProject();
        return this.httpClient
            .post(this.domainUrl, `/ui/css/fetch`, {
                projectId: project.projectId,
            })
            .pipe(
                tap((response: any) => {
                    this.cssVersionData = response;
                    this.cssObject = response;
                }),
            );
    }

    saveTheme(themeConfig: IThemeConfig) {
        const project = this.projectService.fetchCurrentProject();
        if (themeConfig.theme?.logo && !themeConfig.theme.logo.file) themeConfig.theme.logo = null;
        const newObj = { ...themeConfig, projectId: project.projectId };
        return this.httpClient.post(this.domainUrl, `/ui/theme/save`, newObj);
    }

    fetchTheme(): Observable<any> {
        const project = this.projectService.fetchCurrentProject();
        return this.httpClient.post(this.domainUrl, `/ui/theme/fetch`, {
            projectId: project.projectId,
        });
    }

    saveFonts(fontsConfig: { version: string; fonts: any }) {
        const project = this.projectService.fetchCurrentProject();
        return this.httpClient.post(this.domainUrl, `/ui/font/save`, {
            projectId: project.projectId,
            ...fontsConfig,
        });
    }

    fetchFonts(): Observable<any> {
        const project = this.projectService.fetchCurrentProject();
        return this.httpClient.post(this.domainUrl, `/ui/font/fetch`, {
            projectId: project.projectId,
        });
    }

    async fetchIcons() {
        if (this.icons?.length > 0) return this.icons;
        this.icons = await lastValueFrom(this.httpClient.get(this.domainUrl, `/ui/icon/names`));
        return this.icons;
    }

    appendStyles() {
        const currentProject = this.projectService.fetchCurrentProject();
        if (currentProject) {
            const projectId = currentProject?.projectId;
            //append style classes[selector styles] to canvas view
            this.createLink(`/ui/css/stylesheet/project/${projectId}`);
            this.createLink(`/ui/css/variables/project/${projectId}`);
        }
    }

    private createLink(url: string) {
        const link = document.createElement('link') as HTMLLinkElement;
        let href = environment.ALPHA_UI_CONFIG_SERVICE_URL + url;
        const branch = this.branchStateService.getBranch();
        if (branch) {
            href += `?branch=${encodeURIComponent(branch)}`;
        }
        link.setAttribute('href', href);
        link.setAttribute('type', 'text/css');
        link.setAttribute('rel', 'stylesheet');
        link.setAttribute('alphaGlobalStyle', '');
        document.head.appendChild(link);
    }
}
